import { useDashboardSearchParams as useSearchParams } from "@/hooks/useDashboardNavigation";
import {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  useMemo,
  startTransition,
} from "react";
import { useDashboardAppRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useValueChanged } from "@/hooks/useValueChanged";

export type StatusFilterType = "failed" | "pass" | "skipped";

export type BranchFilterType =
  | "all"
  | "main"
  | "current"
  | "main_and_current"
  | "latest_per_branch";

interface TestCheck {
  test_status: "pass" | "fail" | "skip" | "error";
}

interface StoredTestFilters {
  statusFilters: StatusFilterType[];
  timeRangeHours: number;
  branchFilter: BranchFilterType;
  zoomStart: number | null;
  zoomEnd: number | null;
}

interface StoredTestTabState {
  statusFilters: StatusFilterType[];
  selectedTestId?: string | null;
}

function hasAnyTestFailures(checks: TestCheck[]): boolean {
  return checks.some(
    (test) => test.test_status === "fail" || test.test_status === "error",
  );
}

export function parseStatusFilters(
  statusParam: string | null,
): StatusFilterType[] {
  if (!statusParam) return [];
  return statusParam
    .split(",")
    .filter((f): f is StatusFilterType =>
      ["failed", "pass", "skipped"].includes(f),
    );
}

function getSessionStorageKey(jobId: string): string {
  return `testTabState_${jobId}`;
}

function loadFromSessionStorage(jobId: string): StoredTestTabState | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = sessionStorage.getItem(getSessionStorageKey(jobId));
    if (!stored) return null;
    const parsed = JSON.parse(stored) as StoredTestTabState;
    return parsed;
  } catch {
    return null;
  }
}

function saveToSessionStorage(jobId: string, state: StoredTestTabState): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(getSessionStorageKey(jobId), JSON.stringify(state));
  } catch {
    // sessionStorage may not be available in some contexts
  }
}

// Available time range presets in hours (ordered smallest to largest)
// Note: 3h is available in UI but never auto-selected (12h is the minimum default)
const TIME_RANGE_PRESETS = [12, 24, 48, 120, 336] as const;
const MIN_DEFAULT_TIME_RANGE_HOURS = 12;

/**
 * Compute the default time range preset based on job age.
 * Returns the smallest preset that includes the job timestamp, with a floor of 12h.
 *
 * Examples:
 * - Run from ~5 minutes ago → 12h
 * - Run from ~1.5 days ago → 48h (2D)
 * - Run from ~10 days ago → 336h (14D)
 */
function computeDefaultTimeRangeHours(
  jobCompletedAt: string | null | undefined,
): number {
  if (!jobCompletedAt) {
    // Default to 24h if no timestamp available
    return 24;
  }

  const completedDate = new Date(jobCompletedAt);
  if (isNaN(completedDate.getTime())) {
    // Invalid date, fallback to 24h
    return 24;
  }

  const now = new Date();
  const elapsedMs = now.getTime() - completedDate.getTime();
  const elapsedHours = Math.max(0, Math.ceil(elapsedMs / (1000 * 60 * 60)));

  // The required range is the max of the minimum (12h) and the elapsed time
  const requiredHours = Math.max(MIN_DEFAULT_TIME_RANGE_HOURS, elapsedHours);

  // Find the smallest preset that covers the required hours
  for (const preset of TIME_RANGE_PRESETS) {
    if (preset >= requiredHours) {
      return preset;
    }
  }

  // If older than all presets, use the largest one
  return TIME_RANGE_PRESETS[TIME_RANGE_PRESETS.length - 1];
}

function initializeFilters(
  searchParams: URLSearchParams,
  jobId: string,
  checks: TestCheck[],
  isTestTab: boolean,
  jobCompletedAt?: string | null,
): {
  filters: StoredTestFilters;
  selectedTestId: string | null;
} {
  const urlTestId = searchParams.get("test_id");
  const urlStatus = searchParams.get("test_status");

  // Compute time range based on job age (smallest preset that includes the run, min 12h)
  const defaultTimeRangeHours = computeDefaultTimeRangeHours(jobCompletedAt);

  // Priority 1: URL params exist
  if (urlTestId || urlStatus) {
    return {
      filters: {
        statusFilters: parseStatusFilters(urlStatus),
        timeRangeHours: defaultTimeRangeHours,
        branchFilter: "all",
        zoomStart: null,
        zoomEnd: null,
      },
      selectedTestId: urlTestId,
    };
  }

  // Priority 2: Session storage - always load filters so they're ready when switching tabs
  // This prevents the flash of unfiltered tests when switching from logs to tests tab
  const stored = loadFromSessionStorage(jobId);
  if (stored && stored.statusFilters.length > 0) {
    return {
      filters: {
        statusFilters: stored.statusFilters,
        timeRangeHours: defaultTimeRangeHours,
        branchFilter: "all",
        zoomStart: null,
        zoomEnd: null,
      },
      // Only restore selectedTestId if on tests tab
      selectedTestId: isTestTab ? stored.selectedTestId ?? null : null,
    };
  }

  // Priority 3: Default to "failed" if checks already loaded with failures
  // Apply even when not on tests tab so filters are ready when switching
  if (checks.length > 0 && hasAnyTestFailures(checks)) {
    return {
      filters: {
        statusFilters: ["failed"],
        timeRangeHours: defaultTimeRangeHours,
        branchFilter: "all",
        zoomStart: null,
        zoomEnd: null,
      },
      selectedTestId: null,
    };
  }

  // Priority 4: No filters (checks not loaded yet)
  return {
    filters: {
      statusFilters: [],
      timeRangeHours: defaultTimeRangeHours,
      branchFilter: "all",
      zoomStart: null,
      zoomEnd: null,
    },
    selectedTestId: null,
  };
}

export function useTestFilters(
  jobId: string,
  checks: TestCheck[],
  isLoading: boolean,
  isTestTab: boolean,
  jobCompletedAt?: string | null,
) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [defaultFilterApplied, setDefaultFilterApplied] = useState(false);
  // Track if we've already updated the URL for the default filter
  const hasUpdatedUrlForDefaultFilter = useRef(false);
  const isInitialMount = useRef(true);
  const prevChecksLengthRef = useRef(0);
  // Track if user has manually changed the time range (to avoid overriding their choice)
  const [hasUserChangedTimeRange, setHasUserChangedTimeRange] = useState(false);
  // Track the jobCompletedAt we used for initial/latest time range calculation
  const [appliedJobCompletedAt, setAppliedJobCompletedAt] =
    useState(jobCompletedAt);

  // Use lazy initialization to only compute initial state once
  const [filters, setFilters] = useState<StoredTestFilters>(() => {
    const state = initializeFilters(
      searchParams,
      jobId,
      checks,
      isTestTab,
      jobCompletedAt,
    );
    return state.filters;
  });

  const [selectedTestId, setSelectedTestIdState] = useState<string | null>(
    () => {
      const state = initializeFilters(
        searchParams,
        jobId,
        checks,
        isTestTab,
        jobCompletedAt,
      );
      return state.selectedTestId;
    },
  );

  // Reset and re-initialize when jobId changes
  const jobChanged = useValueChanged(jobId);
  if (jobChanged) {
    const newState = initializeFilters(
      searchParams,
      jobId,
      checks,
      isTestTab,
      jobCompletedAt,
    );
    setDefaultFilterApplied(false);
    setHasUserChangedTimeRange(false);
    setAppliedJobCompletedAt(jobCompletedAt);
    setFilters(newState.filters);
    setSelectedTestIdState(newState.selectedTestId);
  }
  useLayoutEffect(() => {
    hasUpdatedUrlForDefaultFilter.current = false;
    isInitialMount.current = true;
    prevChecksLengthRef.current = 0;
  }, [jobId]);

  // Update time range when jobCompletedAt becomes available (late-arriving
  // timestamp), unless the user has picked a time range or zoomed.
  if (
    !jobChanged &&
    !hasUserChangedTimeRange &&
    appliedJobCompletedAt !== jobCompletedAt &&
    jobCompletedAt &&
    filters.zoomStart === null &&
    filters.zoomEnd === null
  ) {
    const newTimeRangeHours = computeDefaultTimeRangeHours(jobCompletedAt);
    setAppliedJobCompletedAt(jobCompletedAt);
    setFilters((prev) => ({
      ...prev,
      timeRangeHours: newTimeRangeHours,
    }));
  }

  // Determine if we need to show loading while waiting for filter determination
  // This prevents flicker where all tests show before filter is applied
  const urlTestId = searchParams.get("test_id");
  const urlStatus = searchParams.get("test_status");
  const hasUrlParams = !!(urlTestId || urlStatus);

  // Sync with URL params when they change externally (e.g., browser
  // back/forward, or entering the tests tab). Only params that are present
  // are adopted: a missing param never clears a selection or filter made in
  // the UI, and local changes are never reverted to a URL that hasn't caught
  // up yet (test_id is written with history.replaceState, status via an
  // async router.replace).
  const urlSyncKey = `${isTestTab}\0${urlTestId ?? ""}\0${urlStatus ?? ""}`;
  if (useValueChanged(urlSyncKey) && !jobChanged && isTestTab) {
    if (urlTestId && urlTestId !== selectedTestId) {
      setSelectedTestIdState(urlTestId);
    }
    if (urlStatus) {
      const urlStatusFilters = parseStatusFilters(urlStatus);
      const currentStatusStr = [...filters.statusFilters].sort().join(",");
      const urlStatusStr = [...urlStatusFilters].sort().join(",");
      if (urlStatusStr !== currentStatusStr) {
        setFilters((prev) => ({
          ...prev,
          statusFilters: urlStatusFilters,
        }));
      }
    }
  }

  const isPendingDefaultFilter = useMemo(() => {
    // Not pending if:
    // - Not on test tab
    // - Still loading checks
    // - No checks available
    // - URL params exist (they override everything)
    // - We already applied default filter
    // - Filters are already set (from session storage or URL)
    if (!isTestTab || isLoading || checks.length === 0 || hasUrlParams) {
      return false;
    }

    // If we have filters set, we're not pending
    if (filters.statusFilters.length > 0) {
      return false;
    }

    // If we already applied default filter, we're not pending
    if (defaultFilterApplied) {
      return false;
    }

    // We're pending if there are failures that need default filter
    return hasAnyTestFailures(checks);
  }, [
    isTestTab,
    isLoading,
    checks,
    hasUrlParams,
    filters.statusFilters.length,
    defaultFilterApplied,
  ]);

  // Track previous isTestTab state to detect tab switches
  const prevIsTestTabRef = useRef(isTestTab);

  const hasFailures = useMemo(() => hasAnyTestFailures(checks), [checks]);

  // Re-initialize filters synchronously when checks become available
  // useLayoutEffect runs synchronously before browser paint, preventing flicker
  // Apply even when not on tests tab so filters are ready when switching tabs
  useLayoutEffect(() => {
    // Skip if URL params exist (they take precedence)
    if (hasUrlParams) {
      prevChecksLengthRef.current = checks.length;
      return;
    }

    // If checks aren't available yet, reset baseline and bail.
    if (checks.length === 0) {
      prevChecksLengthRef.current = 0;
      return;
    }

    // Important: checks can arrive before isLoading flips false.
    // If we update prevChecksLengthRef while still loading, we'd lose the
    // "just loaded" signal and never apply the default filter.
    if (isLoading) {
      return;
    }

    // Apply default filter if:
    // 1. Checks just loaded (prevChecksLengthRef.current === 0)
    // AND we haven't applied default filter yet
    // AND there are no current filters
    // AND there are failures
    // Note: Apply even when not on tests tab so filters are ready when switching
    const checksJustLoaded =
      prevChecksLengthRef.current === 0 && checks.length > 0;
    const shouldApplyDefault =
      checksJustLoaded &&
      !defaultFilterApplied &&
      filters.statusFilters.length === 0 &&
      hasFailures;

    if (shouldApplyDefault) {
      setFilters((prev) => {
        return { ...prev, statusFilters: ["failed"] };
      });
      setDefaultFilterApplied(true);
    }

    prevChecksLengthRef.current = checks.length;
  }, [
    jobId,
    checks.length,
    hasFailures,
    isLoading,
    isTestTab,
    filters.statusFilters.length,
    hasUrlParams,
    defaultFilterApplied,
  ]);

  // Track if we're currently updating URL to prevent concurrent updates
  const isUpdatingUrlRef = useRef(false);

  // Update test_id in URL using history.replaceState (no Next navigation)
  // This avoids triggering re-renders through useSearchParams consumers
  const updateTestIdUrl = useCallback(
    (testId: string | null) => {
      if (typeof window === "undefined") return;

      const params = new URLSearchParams(window.location.search);
      // Ensure tab is correct when interacting with tests.
      // This prevents races where another URL update (e.g. status sync) could
      // otherwise preserve an old tab value like tab=logs.
      if (isTestTab) {
        params.set("tab", "tests");
      }
      if (testId) {
        params.set("test_id", testId);
      } else {
        params.delete("test_id");
      }

      const hash = window.location.hash;
      const newUrl = `${window.location.pathname}?${params.toString()}${hash}`;

      // Use history.replaceState directly - no Next navigation, no re-renders
      window.history.replaceState(window.history.state, "", newUrl);
    },
    [isTestTab],
  );

  // Update status filter URL params using Next router (triggers navigation for filter changes)
  // Note: We read searchParams fresh from the hook to avoid stale closures,
  // but we don't depend on it to prevent circular dependency loops
  const updateStatusUrl = useCallback(
    (status: string | null) => {
      // Prevent concurrent router.replace calls
      if (isUpdatingUrlRef.current) {
        return;
      }

      // Read current params from window.location to avoid stale searchParams
      const params = new URLSearchParams(window.location.search);
      // Ensure tab is correct when interacting with tests.
      // This prevents races where status updates could preserve tab=logs.
      if (isTestTab) {
        params.set("tab", "tests");
      }

      if (status) {
        params.set("test_status", status);
      } else {
        params.delete("test_status");
      }

      const hash = window.location.hash;
      const newUrl = `${window.location.pathname}?${params.toString()}${hash}`;

      // Use requestAnimationFrame + startTransition to defer URL update
      // and mark it as low-priority for better responsiveness
      isUpdatingUrlRef.current = true;
      requestAnimationFrame(() => {
        startTransition(() => {
          router.replace(newUrl, { scroll: false });
        });
        // Reset flag after a short delay
        setTimeout(() => {
          isUpdatingUrlRef.current = false;
        }, 50);
      });
    },
    [router, isTestTab],
  );

  // Reset flags when switching away from test tab
  useEffect(() => {
    if (prevIsTestTabRef.current && !isTestTab) {
      // Switched away from test tab - reset flags so they can be reapplied when switching back
      setDefaultFilterApplied(false);
      hasUpdatedUrlForDefaultFilter.current = false;
    }
    prevIsTestTabRef.current = isTestTab;
  }, [isTestTab]);

  // Debounce timer ref for session storage writes
  const sessionStorageDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Save to session storage when filters or selected test change (only on test tab)
  // Debounced to avoid excessive I/O during rapid test selection
  useEffect(() => {
    if (!isTestTab || isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Clear existing debounce timer
    if (sessionStorageDebounceRef.current) {
      clearTimeout(sessionStorageDebounceRef.current);
    }

    // Debounce session storage write by 300ms
    sessionStorageDebounceRef.current = setTimeout(() => {
      saveToSessionStorage(jobId, {
        statusFilters: filters.statusFilters,
        selectedTestId: selectedTestId,
      });
    }, 300);

    // Cleanup on unmount or dependency change
    return () => {
      if (sessionStorageDebounceRef.current) {
        clearTimeout(sessionStorageDebounceRef.current);
      }
    };
  }, [jobId, isTestTab, filters.statusFilters, selectedTestId]);

  // Listen for popstate (browser back/forward) to sync test_id selection
  // Since we use history.replaceState for test_id, we need a dedicated listener
  useEffect(() => {
    if (typeof window === "undefined" || !isTestTab) return;

    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const newTestId = params.get("test_id");

      // Only sync if test_id is present and different from current state
      // Don't clear selection when URL lacks test_id (user may have navigated back to a page without it)
      if (newTestId && newTestId !== selectedTestId) {
        setSelectedTestIdState(newTestId);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isTestTab, selectedTestId]);

  // Ensure URL has a status param when we have an in-memory status filter (deferred to avoid blocking render)
  // This is especially important for default/session-storage filters, since those may be applied without a user click.
  useEffect(() => {
    const urlStatus = searchParams.get("test_status");

    // Only update URL when on tests tab to avoid polluting URL on other tabs
    if (!isTestTab) {
      return;
    }

    // If URL doesn't have status but we have an in-memory filter, sync it once.
    // Note: We don't check for urlTestId here because we want to add status even if test_id exists.
    if (
      !hasUpdatedUrlForDefaultFilter.current &&
      !urlStatus &&
      filters.statusFilters.length > 0
    ) {
      hasUpdatedUrlForDefaultFilter.current = true;
      const statusValue = filters.statusFilters.join(",");
      // Use requestAnimationFrame to defer URL update after render
      requestAnimationFrame(() => {
        updateStatusUrl(statusValue);
      });
    }
  }, [filters.statusFilters, isTestTab, searchParams, updateStatusUrl]);

  // Wrapper for setSelectedTestId that also updates URL
  // Uses history.replaceState for test_id to avoid Next navigation overhead
  // Also includes status if default filter was applied but not yet synced to URL
  const setSelectedTestId = useCallback(
    (testId: string | null) => {
      setSelectedTestIdState(testId);

      // Only update URL when on tests tab to avoid polluting URL on other tabs
      // Use isTestTab prop instead of reading from URL to avoid race conditions
      if (!isTestTab) {
        return;
      }

      // Check if we need to include status in the URL update
      // This happens when default filter was just applied
      // We use refs instead of state to avoid stale closure issues
      const currentParams = new URLSearchParams(window.location.search);
      const currentStatus = currentParams.get("test_status");
      // Include status if:
      // 1. URL doesn't have status yet
      // 2. We have exactly the "failed" filter (either from default or session storage)
      // 3. We haven't already updated URL with status
      const hasFailedFilter =
        filters.statusFilters.length === 1 &&
        filters.statusFilters[0] === "failed";
      const shouldIncludeStatus =
        !currentStatus &&
        hasFailedFilter &&
        !hasUpdatedUrlForDefaultFilter.current;

      if (shouldIncludeStatus) {
        hasUpdatedUrlForDefaultFilter.current = true;
        // Update test_id via replaceState (fast, no re-renders)
        updateTestIdUrl(testId);
        // Update status via Next router (triggers navigation for filter URL)
        updateStatusUrl("failed");
      } else {
        // Fast path: only update test_id via replaceState (no Next navigation)
        updateTestIdUrl(testId);
      }
    },
    [isTestTab, updateTestIdUrl, updateStatusUrl, filters.statusFilters],
  );

  const handleStatusFiltersChange = useCallback(
    (newStatusFilters: StatusFilterType[]) => {
      setFilters((prev) => ({
        ...prev,
        statusFilters: newStatusFilters,
      }));
      // Update URL via Next router (filter changes should trigger navigation)
      updateStatusUrl(
        newStatusFilters.length > 0 ? newStatusFilters.join(",") : null,
      );
    },
    [updateStatusUrl],
  );

  const handleTimeRangeChange = useCallback((hours: number) => {
    setHasUserChangedTimeRange(true);
    setFilters((prev) => ({
      ...prev,
      timeRangeHours: hours,
    }));
  }, []);

  const handleBranchFilterChange = useCallback((value: BranchFilterType) => {
    setFilters((prev) => ({
      ...prev,
      branchFilter: value,
    }));
  }, []);

  const handleZoomChange = useCallback(
    (start: number | null, end: number | null) => {
      setFilters((prev) => ({
        ...prev,
        zoomStart: start,
        zoomEnd: end,
      }));
    },
    [],
  );

  const clearUrlParams = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete("test_id");
    params.delete("test_status");
    const hash = window.location.hash;
    const newUrl = `${window.location.pathname}?${params.toString()}${hash}`;
    router.replace(newUrl, { scroll: false });
  }, [router, searchParams]);

  return {
    statusFilters: filters.statusFilters,
    timeRangeHours: filters.timeRangeHours,
    branchFilter: filters.branchFilter,
    zoomStart: filters.zoomStart,
    zoomEnd: filters.zoomEnd,
    selectedTestId,
    setSelectedTestId,
    handleStatusFiltersChange,
    handleTimeRangeChange,
    handleBranchFilterChange,
    handleZoomChange,
    clearUrlParams,
    // True when we're waiting to determine default filters (prevents flicker)
    isPendingDefaultFilter,
  };
}
